import type { ChartResult, EvidenceItem, ResultFact, ResultSection } from '../domain/types';

export interface SharePayload {
  version: 1;
  moduleId: ChartResult['moduleId'];
  moduleName: string;
  generatedAt: string;
  question?: string;
  headline: string;
  summary: ResultFact[];
  sections: ResultSection[];
  evidence: EvidenceItem[];
  warnings: string[];
}

export interface ShareQrCode {
  size: number;
  data: Uint8Array;
}

const MAX_SHARE_CHARS = 12_000;

function safeJson(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    const json = JSON.stringify(value, (_key, item: unknown) => {
      if (typeof item === 'function') return undefined;
      if (item && typeof item === 'object') {
        if (seen.has(item)) return '[循环引用]';
        seen.add(item);
      }
      return item;
    }, 2);
    return json.length > 100_000 ? `${json.slice(0, 100_000)}\n…已省略过长数据` : json;
  } catch {
    return '原始数据无法序列化。';
  }
}

function factLines(facts: ResultFact[]): string {
  return facts.map((fact) => `- ${fact.label}：${fact.value}`).join('\n');
}

export function formatResultAsText(result: ChartResult): string {
  const date = new Intl.DateTimeFormat('zh-CN', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(result.generatedAt));
  const sections = result.sections.map((section) => `【${section.title}】\n${factLines(section.facts)}`).join('\n\n');
  const evidence = result.evidence.map((item) => `- ${item.source}：${item.rule}${item.note ? `（${item.note}）` : ''}`).join('\n');
  const warnings = result.warnings.length ? result.warnings.map((item) => `- ${item}`).join('\n') : '- 无';
  return [
    '易鉴｜传统术数研究结果',
    `术数：${result.moduleName}`,
    `生成时间：${date}`,
    `标题：${result.headline}`,
    result.question ? `问题：${result.question}` : '',
    '',
    '【摘要】',
    factLines(result.summary),
    sections,
    '【规则出处】',
    evidence || '- 未附规则出处',
    '【限制与提醒】',
    warnings,
    '',
    '本内容由易鉴程序生成；如包含 AI 解读，可能存在错误或遗漏，仅供传统文化研究与娱乐，不构成现实决策依据。',
    '',
    '【原始数据】',
    safeJson(result.raw)
  ].filter(Boolean).join('\n');
}

export function createSharePayload(result: ChartResult): SharePayload {
  return {
    version: 1,
    moduleId: result.moduleId,
    moduleName: result.moduleName,
    generatedAt: result.generatedAt,
    ...(result.question ? { question: result.question } : {}),
    headline: result.headline,
    summary: result.summary,
    sections: result.sections,
    evidence: result.evidence,
    warnings: result.warnings
  };
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

function base64UrlToBytes(value: string): Uint8Array {
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function createShareUrl(result: ChartResult, origin = window.location.origin, pathname = window.location.pathname): string {
  const payload = bytesToBase64Url(new TextEncoder().encode(JSON.stringify(createSharePayload(result))));
  const url = `${origin}${pathname}#share=${payload}`;
  if (url.length > MAX_SHARE_CHARS) throw new Error('分享链接过长，请改用文字或 PDF 导出。');
  return url;
}

/**
 * 在当前浏览器生成二维码矩阵，不把分享内容发送到第三方二维码网站。
 * 动态加载库，避免二维码功能进入首屏公共包。
 */
export async function createShareQrCode(value: string): Promise<ShareQrCode> {
  const { create } = await import('qrcode');
  const qr = create(value, { errorCorrectionLevel: 'M' });
  return { size: qr.modules.size, data: qr.modules.data };
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isFactArray(value: unknown): value is ResultFact[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'object' && item !== null && typeof (item as ResultFact).label === 'string' && typeof (item as ResultFact).value === 'string');
}

function isSectionArray(value: unknown): value is ResultSection[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'object' && item !== null && typeof (item as ResultSection).title === 'string' && isFactArray((item as ResultSection).facts));
}

function isEvidenceArray(value: unknown): value is EvidenceItem[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'object' && item !== null && typeof (item as EvidenceItem).source === 'string' && typeof (item as EvidenceItem).rule === 'string');
}

export function parseSharePayload(value: string): SharePayload | null {
  if (!value || value.length > MAX_SHARE_CHARS) return null;
  try {
    const parsed = JSON.parse(new TextDecoder().decode(base64UrlToBytes(value))) as Partial<SharePayload>;
    if (parsed.version !== 1 || typeof parsed.moduleId !== 'string' || typeof parsed.moduleName !== 'string' || typeof parsed.generatedAt !== 'string' || Number.isNaN(Date.parse(parsed.generatedAt)) || typeof parsed.headline !== 'string') return null;
    if (!isFactArray(parsed.summary) || !isSectionArray(parsed.sections) || !isEvidenceArray(parsed.evidence) || !isStringArray(parsed.warnings)) return null;
    if (parsed.question !== undefined && typeof parsed.question !== 'string') return null;
    return parsed as SharePayload;
  } catch {
    return null;
  }
}

export function readSharePayload(hash = window.location.hash): SharePayload | null {
  const match = hash.match(/^#share=([^&]+)$/);
  return match ? parseSharePayload(match[1]) : null;
}

export function downloadTextFile(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
